import React, { Component } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Form } from 'react-bootstrap';
import i18n from 'i18n';
import { Service } from 'types/rest/assetCatalogTypes';
import { connect } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import { WithTranslation, withTranslation } from 'react-i18next';
import { ServicesState } from 'store/services/servicesTypes';
import { handleFieldChange } from 'utils/mockServiceUtils';
import axios from 'axios/axios-base';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { reloadWorkspaceComponents } from 'store/blazeUserConfig/blazeUserConfigActions';
import _ from 'lodash';
import { formattedText } from 'utils/commonUtil';
import { BlazeMeterApiResponse } from 'types/rest/blazeMeterApiResponse';
import Autofocus from 'components/forms/Autofocus/Autofocus';

interface LocalProps {
  service: Service | null;
  closeModalAction: (keepModalOpen: boolean) => void;
  createServiceCallBack: (service: Service) => void;
}

class EditServiceModal extends Component<AllProps> {
  private SERVICE_NAME_MAX_LENGTH = 250;

  state = {
    serviceName: this.props.service ? this.props.service.name : '',
    fullMatchFound: '',
    isServiceNameValid: true,
    invalidErrorMessage: '',
  };

  /**
   * Search input field value has changed handler.
   * @param event
   */
  handleNameChange = (event: any) => {
    const elements = handleFieldChange(event, this.SERVICE_NAME_MAX_LENGTH, this.props.services);
    let invalidErrorMessage = `${i18n.t('servicePicker.serviceNameIsInvalid')} ${elements.invalidErrorMessage}`;
    let isServiceNameValid = elements.isServiceNameValid;
    if (elements.fullMatchFound) {
      invalidErrorMessage = i18n.t('servicePicker.serviceNameAlreadyExists');
      isServiceNameValid = false;
    }

    this.setState({
      serviceName: elements.searchTerm,
      fullMatchFound: elements.fullMatchFound,
      isServiceNameValid: isServiceNameValid,
      invalidErrorMessage: invalidErrorMessage,
    });
  };

  errorHandler = (error: any, messagePrefix: string) => {
    this.props.closeModalAction(true);
    const errorMsg = _.get(error, 'response.data.error', error.toString());
    //If the service name is occupied, do inline validation. Otherwise show error toast
    if (errorMsg.includes('already exists.')) {
      this.setState({
        isServiceNameValid: false,
        invalidErrorMessage: `Service ${this.state.serviceName} already exists.`,
      });
    } else {
      this.props.showToastNotification(ToastType.Danger, messagePrefix + this.state.serviceName, errorMsg);
    }
  };

  editServiceHandler = async () => {
    if (!_.isNil(this.props.service) && this.props.service.name !== this.state.serviceName) {
      try {
        await axios.patch(`/workspaces/{workspaceId}/services/${this.props.service.id}`, {
          name: this.state.serviceName,
        });
        this.props.showToastNotification(
          ToastType.Success,
          '',
          formattedText(i18n.t('servicePicker.successRename'), this.state.serviceName),
        );
        if (this.props.service.id) {
          this.props.reloadWorkspaceComponents(this.props.service.id);
        }
        this.props.closeModalAction(false);
      } catch (error) {
        this.errorHandler(error, 'Could not rename service');
      }
    } else {
      this.props.closeModalAction(false);
    }
  };

  createServiceHandler = async () => {
    if (this.state.serviceName.trim().length > 0) {
      try {
        const activeWorkspaceId = this.props.blazeUserConfig?.user.activeWorkspaceId;
        const response = await axios.post('/workspaces/{workspaceId}/services', {
          name: this.state.serviceName,
          workspaceId: activeWorkspaceId,
        });
        const statusCode = response.status;
        // when service didn't exist and was it just created endpoint returns 201.
        // but it returns 200 when service already exist and nothing was created so we treat it as error
        if (statusCode === 200) {
          throw new Error('Service already exists.');
        }
        const data: BlazeMeterApiResponse = response.data;
        const result = data.result as Service;
        this.props.showToastNotification(
          ToastType.Success,
          '',
          formattedText(i18n.t('successMsgs.serviceCreated'), this.state.serviceName),
        );

        this.props.createServiceCallBack(result);
        this.props.closeModalAction(false);
      } catch (error) {
        this.errorHandler(error, 'Could not create new service');
      }
    } else {
      this.props.closeModalAction(false);
    }
  };

  onCancel = () => {
    this.props.closeModalAction(false);
  };

  warningMessages = () => {
    return (
      <div className="invalid-feedback" style={{ visibility: this.state.isServiceNameValid ? 'hidden' : 'visible' }}>
        {this.state.invalidErrorMessage}
      </div>
    );
  };

  render() {
    const isValid = this.state.isServiceNameValid ? '' : 'is-invalid';
    return (
      <Modal
        id="qa-modal-bulk-deploy-modal"
        show={true}
        centered={true}
        backdrop={true}
        onHide={() => this.onCancel()}
        size="sm"
      >
        <Modal.Header closeButton id="qa-rename-service-modal-btn-close-icon">
          {_.isNil(this.props.service) ? (
            <h5 className="modal-title">{i18n.t('common:servicePicker.addNewService')}</h5>
          ) : (
            <h5 className="modal-title">{i18n.t('common:servicePicker.renameService')}</h5>
          )}
        </Modal.Header>
        <Modal.Body style={{ height: '90px' }}>
          <Form.Group>
            <Form.Label htmlFor={'service-rename-field'}>{i18n.t('common:servicePicker.serviceName')}</Form.Label>
            <Autofocus>
              <Form.Control
                className={isValid}
                id={'service-rename-field'}
                placeholder={i18n.t('common:servicePicker.serviceName')}
                onChange={this.handleNameChange}
                value={this.state.serviceName}
                autoComplete={'off'}
              />
            </Autofocus>
          </Form.Group>
          {this.warningMessages()}
        </Modal.Body>
        <Modal.Footer>
          <Button id={'qa-rename-service-modal-btn-cancel'} variant="link" onClick={this.onCancel}>
            {i18n.t('common:servicePicker.cancel')}
          </Button>
          <Button
            id={'qa-rename-service-save'}
            onClick={_.isNil(this.props.service) ? this.createServiceHandler : this.editServiceHandler}
            variant="primary"
            disabled={!this.state.isServiceNameValid || this.state.serviceName.trim() === ''}
          >
            {i18n.t('common:servicePicker.save')}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = (state: ApplicationState) => ({
  services: state.services.services,
  isLoading: state.services.isLoading,
  blazeUserConfig: state.blazeUserConfig.blazeUserConfig,
});

const mapDispatchToProps = {
  showToastNotification,
  reloadWorkspaceComponents,
};
type MapToStateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
type AllProps = LocalProps & ServicesState & MapToStateProps & DispatchProps & WithTranslation;

export default withTranslation('common')(connect(mapStateToProps, mapDispatchToProps)(EditServiceModal));
